import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

/**
 * Cloudflare Turnstile anti-bot widget.
 * Usage:
 *   const ref = useRef();
 *   <TurnstileWidget ref={ref} onToken={setToken} />
 *   ref.current?.reset();  // call after failed submission to get a fresh token
 *
 * If REACT_APP_TURNSTILE_SITE_KEY is not configured, the widget renders nothing
 * and immediately reports a synthetic dev-bypass token (must match backend bypass).
 */
const TurnstileWidget = forwardRef(({ onToken, theme = 'light' }, ref) => {
  const widgetRef = useRef(null);
  const [error, setError] = useState(null);

  useImperativeHandle(ref, () => ({
    reset: () => {
      try { widgetRef.current?.reset?.(); } catch (_) {}
      onToken?.(null);
    },
  }));

  // Dev bypass when no key configured
  if (!SITE_KEY) {
    if (typeof onToken === 'function') {
      // Defer to avoid setState during render
      setTimeout(() => onToken('dev-bypass-token'), 0);
    }
    return null;
  }

  return (
    <div data-testid="turnstile-widget" className="my-3">
      <Turnstile
        ref={widgetRef}
        siteKey={SITE_KEY}
        options={{ theme, size: 'flexible' }}
        onSuccess={(token) => {
          setError(null);
          onToken?.(token);
        }}
        onError={() => {
          setError('Erreur de vérification anti-bot. Réessayez.');
          onToken?.(null);
        }}
        onExpire={() => {
          setError('Vérification expirée. Veuillez recommencer.');
          onToken?.(null);
        }}
      />
      {error && <p className="text-xs text-red-600 mt-1" data-testid="turnstile-error">{error}</p>}
    </div>
  );
});

TurnstileWidget.displayName = 'TurnstileWidget';

export default TurnstileWidget;
